/**
 * Lightweight user analytics — tracks page views and feature usage.
 *
 * Events are batched in memory and flushed to the backend every 30 seconds
 * or when the batch reaches 20 events. Uses navigator.sendBeacon on
 * page unload for reliable delivery.
 */

const FLUSH_INTERVAL = 30_000; // 30 seconds
const MAX_BATCH_SIZE = 20;
const API_BASE = import.meta.env.VITE_API_URL || '';
const CSRF_COOKIE_NAME = 'csrfToken';

interface AnalyticsEvent {
  event_type: string;
  event_name: string;
  event_data?: Record<string, any>;
  facility_id?: string;
  session_id?: string;
}

let eventQueue: AnalyticsEvent[] = [];
let sessionId: string | null = null;
let flushTimer: ReturnType<typeof setInterval> | null = null;
let enabled = true;

function getSessionId(): string {
  if (!sessionId) {
    sessionId = sessionStorage.getItem('analytics_session') || crypto.randomUUID();
    sessionStorage.setItem('analytics_session', sessionId);
  }
  return sessionId;
}

function getCsrfToken(): string | null {
  const prefix = `${encodeURIComponent(CSRF_COOKIE_NAME)}=`;
  const cookie = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));
  return cookie ? decodeURIComponent(cookie.slice(prefix.length)) : null;
}

function analyticsHeaders(): Record<string, string> {
  const csrfToken = getCsrfToken();
  return {
    'Content-Type': 'application/json',
    ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
  };
}

async function flush(): Promise<void> {
  if (eventQueue.length === 0 || !enabled) return;

  const batch = eventQueue.splice(0, MAX_BATCH_SIZE);

  try {
    const res = await fetch(`${API_BASE}/api/v1/analytics/events`, {
      method: 'POST',
      credentials: 'include',
      headers: analyticsHeaders(),
      body: JSON.stringify({ events: batch }),
    });
    if (!res.ok && res.status !== 401) {
      // Put events back if server error (not auth failure)
      eventQueue.unshift(...batch);
    }
  } catch {
    // Network error — put events back for retry
    eventQueue.unshift(...batch);
  }
}

/**
 * Initialize the analytics system. Call once on app mount.
 */
export function initAnalytics(): void {
  // Don't track in development unless explicitly enabled
  if (import.meta.env.DEV && !import.meta.env.VITE_ENABLE_ANALYTICS) {
    enabled = false;
    return;
  }

  flushTimer = setInterval(flush, FLUSH_INTERVAL);

  // Flush on page unload
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      if (eventQueue.length > 0) {
        const body = JSON.stringify({ events: eventQueue.splice(0) });
        void fetch(`${API_BASE}/api/v1/analytics/events`, {
          method: 'POST',
          credentials: 'include',
          keepalive: true,
          headers: analyticsHeaders(),
          body,
        });
      }
    }
  });
}

/**
 * Track a page view.
 */
export function trackPageView(pageName: string, facilityId?: string): void {
  if (!enabled) return;
  eventQueue.push({
    event_type: 'page_view',
    event_name: pageName,
    event_data: { path: window.location.pathname },
    facility_id: facilityId,
    session_id: getSessionId(),
  });
  if (eventQueue.length >= MAX_BATCH_SIZE) flush();
}

/**
 * Track a feature click / interaction.
 */
export function trackFeature(featureName: string, data?: Record<string, any>, facilityId?: string): void {
  if (!enabled) return;
  eventQueue.push({
    event_type: 'feature_click',
    event_name: featureName,
    event_data: data,
    facility_id: facilityId,
    session_id: getSessionId(),
  });
  if (eventQueue.length >= MAX_BATCH_SIZE) flush();
}

/**
 * Track a data export (CSV, PDF).
 */
export function trackExport(exportType: string, target: string, facilityId?: string): void {
  if (!enabled) return;
  eventQueue.push({
    event_type: 'export',
    event_name: `${exportType}_${target}`,
    event_data: { format: exportType, target },
    facility_id: facilityId,
    session_id: getSessionId(),
  });
}

/**
 * Track a search action.
 */
export function trackSearch(query: string, resultCount: number): void {
  if (!enabled) return;
  eventQueue.push({
    event_type: 'search',
    event_name: 'search',
    event_data: { query: query.substring(0, 100), result_count: resultCount },
    session_id: getSessionId(),
  });
}

/**
 * Cleanup — call on app unmount.
 */
export function destroyAnalytics(): void {
  if (flushTimer) clearInterval(flushTimer);
  flush();
}
